import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Home, 
  LogIn, 
  UserPlus, 
  LayoutDashboard, 
  FileText, 
  Inbox, 
  Users, 
  MessageSquare, 
  Settings, 
  CreditCard,
  ArrowLeft,
  Map
} from 'lucide-react';

const Sitemap = () => {
  const sitemapSections = [
    {
      title: "Public Pages",
      icon: Home,
      links: [
        { name: "Home", path: "/", icon: Home, description: "Welcome to BravaxForms" },
        { name: "Login", path: "/login", icon: LogIn, description: "Sign in to your account" },
        { name: "Sign Up", path: "/signup", icon: UserPlus, description: "Create a new account" },
        { name: "Reset Password", path: "/reset-password", icon: Settings, description: "Recover your password" },
      ]
    },
    {
      title: "Dashboard & Forms",
      icon: LayoutDashboard,
      links: [
        { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard, description: "View your analytics and overview" },
        { name: "My Forms", path: "/forms", icon: FileText, description: "Manage all your forms" },
        { name: "Form Builder", path: "/form-builder", icon: FileText, description: "Create and edit forms" },
        { name: "Submissions", path: "/submissions", icon: Inbox, description: "View form responses" },
      ]
    },
    {
      title: "Contacts & CRM",
      icon: Users,
      links: [
        { name: "Contacts", path: "/contacts", icon: Users, description: "Manage your contacts" },
        { name: "CRM", path: "/crm", icon: Users, description: "Contact relationship management" },
      ]
    },
    {
      title: "Automation & Settings",
      icon: MessageSquare,
      links: [
        
        { name: "Settings", path: "/settings", icon: Settings, description: "Account and preferences" },
        { name: "Billing", path: "/billing", icon: CreditCard, description: "Manage your subscription" },
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Sitemap | Bravax Forms — AI Form Builder & CRM Platform</title>
        <meta name="description" content="Complete sitemap of Bravax Forms - Find all pages including AI form builder, CRM, and lead management tools." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        {/* Header */}
        <div className="bg-gradient-hero text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/">
              <Button variant="ghost" className="text-white hover:bg-white/10 mb-6">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </Link>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center">
                <Map className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold">Sitemap</h1>
                <p className="text-white/90 text-lg mt-2">
                  Navigate through all pages of BravaxForms
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sitemapSections.map((section, sectionIndex) => (
              <Card 
                key={sectionIndex} 
                className="card-elegant hover:shadow-glow transition-all duration-300"
              >
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <section.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{section.title}</CardTitle>
                      <CardDescription>
                        {section.links.length} page{section.links.length !== 1 ? 's' : ''}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {section.links.map((link, linkIndex) => (
                      <Link 
                        key={linkIndex} 
                        to={link.path}
                        className="block p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all duration-300 group"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                            <link.icon className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                              {link.name}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              {link.description}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* XML Sitemap Link */}
          <Card className="card-elegant mt-8">
            <CardHeader>
              <CardTitle>XML Sitemap</CardTitle>
              <CardDescription>
                For search engines and crawlers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                The machine-readable XML sitemap is available for search engine crawlers and SEO tools.
              </p>
              <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="hover-scale">
                  View XML Sitemap
                  <ArrowLeft className="ml-2 h-4 w-4 rotate-180" />
                </Button>
              </a>
            </CardContent>
          </Card>

          {/* CTA Section */}
          <div className="mt-12 text-center">
            <Card className="card-elegant bg-gradient-hero text-white border-0">
              <CardContent className="pt-8 pb-8">
                <h2 className="text-2xl font-bold mb-3">
                  Ready to start building forms?
                </h2>
                <p className="text-white/90 mb-6">
                  Join thousands of entrepreneurs using BravaxForms
                </p>
                <div className="flex gap-4 justify-center">
                  <Link to="/signup">
                    <Button size="lg" variant="secondary" className="hover-scale">
                      Get Started Free
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button size="lg" variant="outline" className="text-white border-white hover:bg-white/10">
                      Sign In
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sitemap;
